import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 35000
});

apiClient.interceptors.request.use(
  (config) => {
    let token;
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        token = parsedUser?.token || '';
      } catch (error) {
        console.error('Error parsing user token:', error);
      }
    }

    if (token && config.headers) {
      config.headers['AuthorizationToken'] = `${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => response,
  (error) => Promise.reject(error)
);

// Error handling utilities
const isAxiosError = (error) => {
  return axios.isAxiosError(error);
};

const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const message = error.response.data?.message || error.response.data?.error || 'Server error';
    
    switch (status) {
      case 400:
        return { message: 'Invalid request data' };
      case 401:
        return { message: 'Authentication failed. Please login again.' };
      case 403:
        return { message: 'Access denied. You do not have permission for this action.' };
      case 404:
        return { message: 'Resource not found' };
      case 422:
        return { message: 'Validation error: ' + message };
      case 500:
        return { message: 'Internal server error. Please try again later.' };
      default:
        return { message: message || 'An error occurred' };
    }
  } else if (error.request) {
    // Request was made but no response received
    return { message: 'Network error. Please check your connection.' };
  } else {
    // Something else happened
    return { message: 'An unexpected error occurred.' };
  }
};

/**
 * Login credentials interface
 * @typedef {Object} LoginCredentials
 * @property {string} email - User email
 * @property {string} password - User password
 */

/**
 * Authentication response interface
 * @typedef {Object} AuthResponse
 * @property {string} token - JWT token
 * @property {Object} user - User object
 * @property {string} user.id - User ID
 * @property {string} user.email - User email
 * @property {string} user.name - User name
 */

/**
 * Authenticate user with email and password
 * @param {LoginCredentials} credentials - Login credentials
 * @returns {Promise<AuthResponse>} Authentication response
 */
export async function authLogin(credentials) {
  try {
    const response = await apiClient.post('/sessions', credentials);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const friendlyError = handleApiError(error);
      throw new Error(friendlyError.message);
    }
    throw new Error('An unexpected error occurred during login.');
  }
}

/**
 * Logout user (clear local storage)
 * @returns {Promise<void>}
 */
export async function authLogout() {
  try {
    // Call logout endpoint if needed
    await apiClient.post('/sessions/logout');
  } catch (error) {
    // Even if logout API fails, we should clear local storage
    console.warn('Logout API call failed, but clearing local storage:', error);
  } finally {
    // Always clear local storage
    localStorage.removeItem('user');
  }
}

/**
 * Verify current authentication token
 * @returns {Promise<Object>} User data if token is valid
 */
export async function verifyAuth() {
  try {
    const response = await apiClient.get('/sessions/verify');
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const friendlyError = handleApiError(error);
      throw new Error(friendlyError.message);
    }
    throw new Error('An unexpected error occurred during authentication verification.');
  }
}

/**
 * Refresh authentication token
 * @returns {Promise<AuthResponse>} New authentication response
 */
export async function refreshToken() {
  try {
    const response = await apiClient.post('/sessions/refresh');
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const friendlyError = handleApiError(error);
      throw new Error(friendlyError.message);
    }
    throw new Error('An unexpected error occurred during token refresh.');
  }
}

/**
 * Register new user
 * @param {Object} userData - User registration data
 * @param {string} userData.email - User email
 * @param {string} userData.password - User password
 * @param {string} userData.name - User name
 * @returns {Promise<AuthResponse>} Authentication response
 */
export async function authRegister(userData) {
  try {
    const response = await apiClient.post('/users', userData);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const friendlyError = handleApiError(error);
      throw new Error(friendlyError.message);
    }
    throw new Error('An unexpected error occurred during registration.');
  }
}

/**
 * Request password reset
 * @param {string} email - User email
 * @returns {Promise<Object>} Response indicating success
 */
export async function requestPasswordReset(email) {
  try {
    const response = await apiClient.post('/users/password-reset', { email });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const friendlyError = handleApiError(error);
      throw new Error(friendlyError.message);
    }
    throw new Error('An unexpected error occurred during password reset request.');
  }
}

/**
 * Reset password with token
 * @param {string} token - Reset token
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response indicating success
 */
export async function resetPassword(token, newPassword) {
  try {
    const response = await apiClient.post('/users/password-reset/confirm', {
      token,
      password: newPassword
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const friendlyError = handleApiError(error);
      throw new Error(friendlyError.message);
    }
    throw new Error('An unexpected error occurred during password reset.');
  }
}

export default {
  authLogin,
  authLogout,
  verifyAuth,
  refreshToken,
  authRegister,
  requestPasswordReset,
  resetPassword
}; 